/**
 * Emit hosts for the `button` analytics group — 1 function
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type ButtonEventProps } from '@/models/analytics/features/ui';
import { type ButtonAction, type ScreenName } from '@heidi/runtime/analytics-vocabulary';

/**
 * Input shape for `analyticsScribe.trackButtonClicked`.
 *
 * Tightens `ButtonEventProps` (where `screen_name` and `action` are
 * declared optional) so that every dispatch through the helper carries
 * both — neither the snake_case nor the legacy camelCase arm can be
 * called with just a `label`. See helper JSDoc for the policy.
 */
export type TrackButtonClickedInput =
  | (ButtonEventProps & {
      screen_name: ScreenName;
      action: ButtonAction;
      /** @deprecated Pass `screen_name` directly. */
      screenName?: ScreenName;
      /** @deprecated Pass `task_id` directly. */
      taskId?: string;
      /** @deprecated Pass `click_source` directly. */
      clickSource?: string;
    })
  | (ButtonEventProps & {
      /** @deprecated Pass `screen_name` directly. */
      screenName: ScreenName;
      action: ButtonAction;
      /** @deprecated Pass `task_id` directly. */
      taskId?: string;
      /** @deprecated Pass `click_source` directly. */
      clickSource?: string;
    });

export function trackButtonClicked(input: TrackButtonClickedInput) {
  const { screenName, taskId, clickSource, ...rest } = input;

  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      ...rest,
      // Legacy camelCase shortcuts win when both shapes are passed; in
      // practice no caller supplies both. Each only overrides when
      // defined so a caller passing only `screen_name` keeps that
      // value instead of silently being undefined-overwritten.
      ...(screenName !== undefined && { screen_name: screenName }),
      ...(taskId !== undefined && { task_id: taskId }),
      ...(clickSource !== undefined && { click_source: clickSource }),
    },
    options: { amplitude: false },
  });
}
